package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Image
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.data.PickedMediaHolder
import org.gymly.model.AdminClient
import org.gymly.model.AdminPayment
import org.gymly.model.AdminPaymentCollect
import org.gymly.model.AdminPaymentCollectLine
import org.gymly.model.AdminPaymentCollectTarget
import org.gymly.model.AdminPaymentMethod
import org.gymly.model.AdminPaymentOrigin
import org.gymly.model.AdminPaymentStatus
import org.gymly.model.AdminPaymentUpsert
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.ui.components.FileUploadIconButton
import org.gymly.ui.components.AdminDateField
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.ProofAttachmentView
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.downloadBytes
import org.gymly.util.formatMoney
import org.gymly.util.openProofFilePicker
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.todayIsoDate
import org.gymly.util.userMessage
import kotlin.math.roundToLong
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import org.gymly.ui.components.VerticalScrollColumn

private enum class PaymentsDateFilter(val label: String, val days: Int?) {
    Days7("7 días", 7),
    Days30("30 días", 30),
    All("Todos", null),
}

private sealed class PaymentsDialog {
    data object Create : PaymentsDialog()
    data class Edit(val payment: AdminPayment) : PaymentsDialog()
    data class Detail(val payment: AdminPayment) : PaymentsDialog()
    data class Delete(val payment: AdminPayment) : PaymentsDialog()
}

private fun eurosToCents(raw: String): Long? {
    val cleaned = raw.trim().replace("€", "").replace(" ", "").replace(",", ".")
    if (cleaned.isEmpty()) return null
    val value = cleaned.toDoubleOrNull() ?: return null
    if (value <= 0) return null
    return (value * 100.0).roundToLong()
}

private fun centsToEurosInput(cents: Long): String {
    val intPart = cents / 100
    val frac = kotlin.math.abs(cents % 100).toInt()
    return "$intPart,${frac.toString().padStart(2, '0')}"
}

@Composable
fun AdminPaymentsScreen() {
    val scope = rememberCoroutineScope()
    val pendingApprovals = LocalPendingApprovalNotifier.current
    var payments by remember { mutableStateOf<List<AdminPayment>>(emptyList()) }
    var clients by remember { mutableStateOf<List<AdminClient>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dateFilter by remember { mutableStateOf(PaymentsDateFilter.All) }
    var dialog by remember { mutableStateOf<PaymentsDialog?>(null) }
    var saving by remember { mutableStateOf(false) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                payments = AdminRepo.api.listPayments()
                clients = AdminRepo.api.listClients().forClientPicker()
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar los pagos.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    val filtered = remember(payments, query, dateFilter) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        payments.filter { p ->
            val matchesQuery = q.isEmpty() ||
                p.clientName.lowercase().contains(q) ||
                p.method.labelEs().lowercase().contains(q) ||
                p.origin.labelEs().lowercase().contains(q) ||
                p.status.labelEs().lowercase().contains(q)
            val matchesDate = if (cutoff == null) {
                true
            } else {
                val created = (p.paidAt ?: p.createdAt)?.let { runCatching { Instant.parse(it) }.getOrNull() }
                created == null || created >= cutoff
            }
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "client",
        resetKey = listOf(query, dateFilter),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "amount" -> { p -> p.amountCents }
                "method" -> { p -> p.method.name }
                "status" -> { p -> p.status.name }
                "date" -> { p -> p.paymentDate() }
                else -> { p -> p.clientName.lowercase() }
            }
        }
    }

    val total = filtered.size
    val autopay = filtered.count { it.origin.isAutopay() }
    val paid = filtered.count { it.status == AdminPaymentStatus.PAID }
    val pending = filtered.count { it.status == AdminPaymentStatus.PENDING }
    val failed = filtered.count { it.status == AdminPaymentStatus.FAILED }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        Column(Modifier.fillMaxSize()) {
            PaymentsHeader(dateFilter) { dateFilter = it }
            Spacer(Modifier.height(16.dp))
            PaymentsKpiRow(total, autopay, paid, pending, failed, compact)
            Spacer(Modifier.height(16.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            PaymentsDirectoryCard(
                payments = pageItems,
                totalCount = filtered.size,
                listPaging = listPaging,
                loading = loading,
                query = query,
                onQueryChange = { query = it },
                compact = compact,
                onCreate = { dialog = PaymentsDialog.Create },
                onView = { dialog = PaymentsDialog.Detail(it) },
                onEdit = { dialog = PaymentsDialog.Edit(it) },
                onDelete = { dialog = PaymentsDialog.Delete(it) },
                onConfirm = { payment ->
                    scope.launch {
                        saving = true
                        error = null
                        try {
                            AdminRepo.api.updatePayment(
                                payment.id,
                                AdminPaymentUpsert(
                                    clientId = payment.clientId,
                                    method = payment.method,
                                    amountCents = payment.amountCents,
                                    status = AdminPaymentStatus.PAID,
                                    currency = payment.currency,
                                    notes = payment.notes,
                                ),
                            )
                            reload()
                        } catch (e: Exception) {
                            error = e.userMessage("No se pudo confirmar el pago.")
                        } finally {
                            saving = false
                        }
                    }
                },
            )
        }
    }

    when (val d = dialog) {
        is PaymentsDialog.Detail -> PaymentDetailDialog(d.payment) { dialog = null }
        is PaymentsDialog.Create -> PaymentFormDialog(
            title = "Registrar pago",
            initial = null,
            clients = clients,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert, lines, proof ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        if (lines != null && lines.isNotEmpty() && upsert?.clientId != null) {
                            val defaultAmount = upsert.amountCents.let { (it / 100.0).toString() }
                            val exchangeQuote = runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull()
                            val created = AdminRepo.api.collectPayments(
                                AdminPaymentCollect(
                                    currency = upsert.currency,
                                    targets = listOf(
                                        AdminPaymentCollectTarget(
                                            clientId = upsert.clientId,
                                            amountCents = upsert.amountCents,
                                        ),
                                    ),
                                    lines = lines.map { line ->
                                        line.toCollectLine(defaultAmount, exchangeQuote)
                                    },
                                    paidAt = upsert.paidAt,
                                ),
                            )
                            lines.forEachIndexed { index, line ->
                                line.proof?.let { p ->
                                    created.getOrNull(index)?.let { payment ->
                                        AdminRepo.api.uploadPaymentProof(payment.id, p)
                                    }
                                }
                            }
                        } else if (upsert != null) {
                            val created = AdminRepo.api.createPayment(upsert)
                            if (proof != null) {
                                val withProof = AdminRepo.api.uploadPaymentProof(created.id, proof)
                                if (withProof.proofUrl.isNullOrBlank()) {
                                    error = "El pago se creó pero el comprobante no quedó guardado. Reintenta editarlo."
                                    reload()
                                    return@launch
                                }
                            }
                        }
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo registrar el pago.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is PaymentsDialog.Edit -> PaymentFormDialog(
            title = "Editar pago",
            initial = d.payment,
            clients = clients,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert, _, proof ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        if (upsert != null) {
                            AdminRepo.api.updatePayment(d.payment.id, upsert)
                        }
                        if (proof != null) {
                            val withProof = AdminRepo.api.uploadPaymentProof(d.payment.id, proof)
                            if (withProof.proofUrl.isNullOrBlank()) {
                                error = "Los datos se guardaron pero el comprobante no. Reintenta."
                                reload()
                                return@launch
                            }
                        }
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo guardar el pago.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is PaymentsDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar pago") },
            text = {
                Text("¿Seguro que deseas eliminar el pago de ${d.payment.clientName}? Esta acción no se puede deshacer.")
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deletePayment(d.payment.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) {
                                    dialog = null
                                    error = null
                                } else {
                                    error = e.userMessage("No se pudo eliminar el pago.")
                                }
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Eliminar")
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun PaymentsHeader(
    dateFilter: PaymentsDateFilter,
    onFilterChange: (PaymentsDateFilter) -> Unit,
) {
    var menuOpen by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text("Pagos", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                "Pagos y su estado desde /api/payments (cliente, método, monto, estado, fecha de pago).",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        Box {
            OutlinedButton(onClick = { menuOpen = true }, shape = RoundedCornerShape(10.dp)) {
                Text(dateFilter.label, color = AdminColors.Text)
            }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                PaymentsDateFilter.entries.forEach { opt ->
                    DropdownMenuItem(
                        text = { Text(opt.label) },
                        onClick = {
                            onFilterChange(opt)
                            menuOpen = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun PaymentsKpiRow(
    total: Int,
    autopay: Int,
    paid: Int,
    pending: Int,
    failed: Int,
    compact: Boolean,
) {
    val kpiItems = listOf(
        Triple("Total de pagos", total.toString(), AdminColors.Primary),
        Triple("Autopagos", autopay.toString(), Color(0xFF7C3AED)),
        Triple("Pagados", paid.toString(), Color(0xFF2E7D32)),
        Triple("Pendientes", pending.toString(), Color(0xFFF59E0B)),
        Triple("Fallidos", failed.toString(), Color(0xFFC62828)),
    )
    if (compact) {
        // Celular: franja de indicadores de una línea.
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            kpiItems.forEach { (label, value, accent) ->
                AdminCompactKpiChip(label, value, accent, Modifier.weight(1f))
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            kpiItems.forEach { (label, value, accent) ->
                PaymentKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun PaymentKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun ColumnScope.PaymentsDirectoryCard(
    payments: List<AdminPayment>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onView: (AdminPayment) -> Unit,
    onEdit: (AdminPayment) -> Unit,
    onDelete: (AdminPayment) -> Unit,
    onConfirm: (AdminPayment) -> Unit,
) {
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Pagos", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                PaymentSearchField(query, onQueryChange)
                Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("+ Registrar pago", fontWeight = FontWeight.SemiBold)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Pagos", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) {
                        PaymentSearchField(query, onQueryChange)
                    }
                    Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text("+ Registrar pago", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("No hay pagos todavía.", color = AdminColors.TextMuted)
            }
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    payments.forEach { p -> PaymentMobileCard(p, onView, onEdit, onDelete, onConfirm) }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                PaymentsTable(
                    payments,
                    listPaging = listPaging,
                    onView,
                    onEdit,
                    onDelete,
                    onConfirm,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun PaymentSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun PaymentsTable(
    payments: List<AdminPayment>,
    listPaging: AdminListPaging,
    onView: (AdminPayment) -> Unit,
    onEdit: (AdminPayment) -> Unit,
    onDelete: (AdminPayment) -> Unit,
    onConfirm: (AdminPayment) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader("Cliente", "client", listPaging.sort, listPaging::onSort, Modifier.weight(1.5f))
            AdminSortableHeader("Monto", "amount", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
            AdminSortableHeader("Método", "method", listPaging.sort, listPaging::onSort, Modifier.weight(1.1f))
            AdminSortableHeader("Fecha de pago", "date", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
            AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.85f))
            AdminListHeaderLabel("Acciones", Modifier.width(180.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth(),
        ) {
            payments.forEach { p ->
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.5f), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(Color(p.status.dotColorArgb())),
                        )
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(p.clientName, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                            Text(p.method.labelEs(), color = AdminColors.TextMuted, fontSize = 12.sp)
                        }
                    }
                    Text(
                        formatMoney(p.amountCents.toInt().coerceAtLeast(0), p.currency),
                        Modifier.weight(0.8f),
                        color = AdminColors.Text,
                        fontSize = 13.sp,
                        maxLines = 1,
                    )
                    Box(Modifier.weight(1.1f)) {
                        Text(p.method.labelEs(), color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
                    }
                    Text(p.paymentDate(), Modifier.weight(1f), color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
                    Box(Modifier.weight(0.85f)) { PaymentStatusBadge(p.status) }
                    Row(Modifier.width(180.dp)) {
                        if (p.status == AdminPaymentStatus.PENDING) {
                            if (canWriteModule(AdminModuleAccess.PAYMENTS)) IconButton(onClick = { onConfirm(p) }) {
                                Icon(Icons.Outlined.Add, "Confirmar", tint = Color(0xFF2E7D32))
                            }
                        }
                        IconButton(onClick = { onView(p) }) {
                            Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                        }
                        if (canWriteModule(AdminModuleAccess.PAYMENTS)) IconButton(onClick = { onEdit(p) }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.Primary)
                        }
                        if (canDeleteModule(AdminModuleAccess.PAYMENTS)) IconButton(onClick = { onDelete(p) }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun PaymentMobileCard(
    payment: AdminPayment,
    onView: (AdminPayment) -> Unit,
    onEdit: (AdminPayment) -> Unit,
    onDelete: (AdminPayment) -> Unit,
    onConfirm: (AdminPayment) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(14.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                Modifier
                    .size(8.dp)
                    .clip(CircleShape)
                    .background(Color(payment.status.dotColorArgb())),
            )
            Spacer(Modifier.width(8.dp))
            Column(Modifier.weight(1f)) {
                Text(payment.clientName, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                Text(payment.method.labelEs(), color = AdminColors.TextMuted, fontSize = 12.sp)
            }
            PaymentStatusBadge(payment.status)
        }
        Spacer(Modifier.height(8.dp))
        Text(
            formatMoney(payment.amountCents.toInt().coerceAtLeast(0), payment.currency),
            color = AdminColors.Text,
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
        )
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
            OriginBadge(payment.origin)
            Text(payment.paymentDate(), color = AdminColors.TextMuted, fontSize = 12.sp)
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            if (payment.status == AdminPaymentStatus.PENDING) {
                if (canWriteModule(AdminModuleAccess.PAYMENTS)) IconButton(onClick = { onConfirm(payment) }) {
                    Icon(Icons.Outlined.Add, null, tint = Color(0xFF2E7D32))
                }
            }
            IconButton(onClick = { onView(payment) }) {
                Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted)
            }
            if (canWriteModule(AdminModuleAccess.PAYMENTS)) IconButton(onClick = { onEdit(payment) }) {
                Icon(Icons.Outlined.Edit, null, tint = AdminColors.Primary)
            }
            if (canDeleteModule(AdminModuleAccess.PAYMENTS)) IconButton(onClick = { onDelete(payment) }) {
                Icon(Icons.Outlined.Delete, null, tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun PaymentStatusBadge(status: AdminPaymentStatus) {
    Text(
        status.labelEs(),
        color = Color(status.badgeFgArgb()),
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(999.dp))
            .background(Color(status.badgeBgArgb()))
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun OriginBadge(origin: AdminPaymentOrigin) {
    Text(
        origin.labelEs(),
        color = Color(origin.badgeFgArgb()),
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(999.dp))
            .background(Color(origin.badgeBgArgb()))
            .padding(horizontal = 8.dp, vertical = 3.dp),
    )
}

@Composable
private fun PaymentDetailDialog(payment: AdminPayment, onDismiss: () -> Unit) {
    val scope = rememberCoroutineScope()
    var busy by remember { mutableStateOf(false) }
    var actionError by remember { mutableStateOf<String?>(null) }
    var actionOk by remember { mutableStateOf<String?>(null) }
    Dialog(onDismissRequest = onDismiss) {
        VerticalScrollColumn(
            Modifier
                .widthIn(max = 420.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Text("Detalle del pago", fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(14.dp))
            DetailRow("Cliente", payment.clientName)
            DetailRow("Método", payment.method.labelEs())
            DetailRow("Monto", formatMoney(payment.amountCents.toInt().coerceAtLeast(0), payment.currency))
            DetailRow("Fecha de pago", payment.paymentDate())
            DetailRow("Origen", payment.origin.labelEs())
            DetailRow("Estado", payment.status.labelEs())
            payment.notes?.takeIf { it.isNotBlank() }?.let { DetailRow("Referencia", it) }
            payment.payerPhone?.takeIf { it.isNotBlank() }?.let { DetailRow("Teléfono pago móvil", it) }
            payment.proofUrl?.takeIf { it.isNotBlank() }?.let { url ->
                Spacer(Modifier.height(10.dp))
                ProofAttachmentView(
                    proofUrl = url,
                    proofContentType = payment.proofContentType,
                    accent = AdminColors.Primary,
                    muted = AdminColors.TextMuted,
                    surface = AdminColors.Surface,
                    text = AdminColors.Text,
                    outline = AdminColors.Outline,
                )
            }
            actionError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 12.sp)
            }
            actionOk?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Primary, fontSize = 12.sp)
            }
            Spacer(Modifier.height(16.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                OutlinedButton(
                    onClick = {
                        scope.launch {
                            busy = true
                            actionError = null
                            actionOk = null
                            try {
                                val file = AdminRepo.api.downloadPaymentReceipt(payment.id)
                                downloadBytes(file.fileName, file.mimeType, file.bytes)
                                actionOk = "Recibo descargado."
                            } catch (e: Throwable) {
                                actionError = e.userMessage("No se pudo descargar el recibo.")
                            } finally {
                                busy = false
                            }
                        }
                    },
                    enabled = !busy,
                ) { Text("Descargar recibo", fontSize = 12.sp) }
                OutlinedButton(
                    onClick = {
                        scope.launch {
                            busy = true
                            actionError = null
                            actionOk = null
                            try {
                                AdminRepo.api.emailPaymentReceipt(payment.id)
                                actionOk = "Recibo enviado por correo."
                            } catch (e: Throwable) {
                                actionError = e.userMessage("No se pudo enviar el recibo.")
                            } finally {
                                busy = false
                            }
                        }
                    },
                    enabled = !busy,
                ) { Text("Enviar mail", fontSize = 12.sp) }
            }
            Spacer(Modifier.height(10.dp))
            Button(
                onClick = onDismiss,
                modifier = Modifier.align(Alignment.End),
                colors = ButtonDefaults.buttonColors(
                    containerColor = AdminColors.Primary,
                    contentColor = AdminColors.OnBrand,
                ),
            ) {
                Text("Cerrar")
            }
        }
    }
}

@Composable
private fun DetailRow(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

private fun clientMatchesPaymentSearch(client: AdminClient, query: String): Boolean =
    clientMatchesPickerSearch(client, query)

private fun filterClientsForPaymentSearch(clients: List<AdminClient>, query: String): List<AdminClient> =
    filterClientsForPickerSearch(clients, query)

private fun normalizePayerPhoneInput(raw: String): String =
    raw.filter { it.isDigit() || it == '+' }

@Composable
private fun PaymentFormDialog(
    title: String,
    initial: AdminPayment?,
    clients: List<AdminClient>,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (AdminPaymentUpsert?, List<PaymentLineDraft>?, PickedImagePayload?) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var clientId by remember(initial) { mutableStateOf(initial?.clientId.orEmpty()) }
    var clientQuery by remember(initial) { mutableStateOf(initial?.clientName.orEmpty()) }
    var method by remember(initial) { mutableStateOf(initial?.method ?: AdminPaymentMethod.CASH) }
    var amountInput by remember(initial) {
        mutableStateOf(initial?.let { centsToEurosInput(it.amountCents) }.orEmpty())
    }
    var draftLines by remember { mutableStateOf(listOf<PaymentLineDraft>()) }
    var draftChangeBsCents by remember { mutableStateOf(0L) }
    var draftChangeUsdCents by remember { mutableStateOf(0L) }
    var status by remember(initial) {
        mutableStateOf(initial?.status ?: (initial?.method ?: AdminPaymentMethod.CARD).defaultCreateStatus())
    }
    var notes by remember(initial) { mutableStateOf(initial?.notes.orEmpty()) }
    var payerPhone by remember(initial) { mutableStateOf(initial?.payerPhone.orEmpty()) }
    var paidDate by remember(initial) {
        mutableStateOf(initial?.paidAt?.take(10) ?: todayIsoLocalDate())
    }
    var proofPayload by remember { mutableStateOf<PickedImagePayload?>(null) }
    var proofName by remember { mutableStateOf<String?>(null) }
    val proofHolder = remember { PickedMediaHolder() }
    var picking by remember { mutableStateOf(false) }
    var formError by remember { mutableStateOf<String?>(null) }
    var clientMenu by remember { mutableStateOf(false) }
    var methodMenu by remember { mutableStateOf(false) }
    var statusMenu by remember { mutableStateOf(false) }
    var registeredRefs by remember { mutableStateOf<Map<String, String>>(emptyMap()) }

    // Referencias de la misma fecha de pago (sin contar este pago al editarlo).
    LaunchedEffect(initial?.id, paidDate) {
        registeredRefs = loadRegisteredPaymentRefs(paidDate, excludePaymentId = initial?.id)
    }

    val mobileRefDuplicateAlert = remember(method, notes, registeredRefs) {
        paymentReferenceDuplicateWarning(
            line = PaymentLineDraft(method = method, notes = notes),
            siblingLines = emptyList(),
            knownMobileRefs = emptySet(),
            registeredRefs = registeredRefs,
        )
    }

    val filteredClients = remember(clients, clientQuery) {
        filterClientsForPaymentSearch(clients, clientQuery)
    }
    val clientPickerOptions = remember(clients, clientQuery, filteredClients) {
        if (clientQuery.trim().isEmpty()) clients.take(12) else filteredClients
    }
    val showClientBrowse = clientMenu && clientQuery.trim().isEmpty() && clientPickerOptions.isNotEmpty()
    val showClientResults = clientMenu && clientQuery.trim().length >= 2 && clientPickerOptions.isNotEmpty()
    val showClientEmpty = clientMenu && clientQuery.trim().length >= 2 && filteredClients.isEmpty()

    Dialog(
        onDismissRequest = { if (!saving) onDismiss() },
        properties = DialogProperties(dismissOnClickOutside = false),
    ) {
        BoxWithConstraints(
            Modifier
                .widthIn(max = 680.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            val twoCol = maxWidth >= 540.dp
            VerticalScrollColumn(Modifier) {
                Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                Spacer(Modifier.height(14.dp))
                FormLabel("Cliente *")
                Text(
                    "Solo socios (clientes activos). Busca por nombre, apellido, correo o cédula.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(bottom = 6.dp),
                )
                Box {
                    OutlinedTextField(
                        value = clientQuery,
                        onValueChange = {
                            clientQuery = it
                            clientMenu = true
                            if (clientId.isNotEmpty()) {
                                val match = clients.firstOrNull { c -> c.id == clientId }
                                if (match == null || !clientMatchesPaymentSearch(match, it)) {
                                    clientId = ""
                                }
                            }
                        },
                        enabled = initial == null,
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        placeholder = { Text("Nombre, apellido, correo o cédula…", color = AdminColors.TextMuted) },
                        leadingIcon = {
                            Icon(Icons.Outlined.Search, contentDescription = "Buscar cliente", tint = AdminColors.TextMuted)
                        },
                        shape = RoundedCornerShape(10.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = AdminColors.Primary,
                            unfocusedBorderColor = AdminColors.Outline,
                        ),
                    )
                    if (initial == null) {
                        DropdownMenu(
                            expanded = showClientBrowse || showClientResults,
                            onDismissRequest = { clientMenu = false },
                        ) {
                            clientPickerOptions.forEach { c ->
                                DropdownMenuItem(
                                    text = {
                                        Column {
                                            Text(c.fullName, fontWeight = FontWeight.Medium)
                                            val subtitle = listOfNotNull(
                                                c.email,
                                                c.docId?.let { "Cédula $it" },
                                            ).joinToString(" · ")
                                            if (subtitle.isNotBlank()) {
                                                Text(subtitle, color = AdminColors.TextMuted, fontSize = 12.sp)
                                            }
                                        }
                                    },
                                    onClick = {
                                        clientId = c.id
                                        clientQuery = c.fullName
                                        clientMenu = false
                                    },
                                )
                            }
                        }
                    }
                }
                if (initial == null) {
                    when {
                        showClientEmpty -> Text(
                            "No hay cliente con ese dato.",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                            modifier = Modifier.padding(top = 6.dp),
                        )
                        clientMenu && clientQuery.trim().length == 1 -> Text(
                            "Escribe al menos 2 caracteres para buscar.",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                            modifier = Modifier.padding(top = 6.dp),
                        )
                    }
                }
                Spacer(Modifier.height(14.dp))
                if (initial == null) {
                    FormLabel("Monto total del plan (Bs.) *")
                    FormField(amountInput, { amountInput = it }, "45,00")
                    Spacer(Modifier.height(10.dp))
                    FormLabel("Fecha del pago *")
                    AdminDateField(
                        value = paidDate,
                        onValueChange = { paidDate = it },
                        placeholder = "AAAA-MM-DD o DD/MM/AAAA",
                        outputFormat = AdminDateOutputFormat.ISO,
                    )
                    Spacer(Modifier.height(14.dp))
                    CombinedPaymentLinesForm(
                        totalAmountInput = amountInput,
                        defaultLineAmount = amountInput,
                        enabled = !saving,
                        checkExistingMobilePayments = true,
                        paymentDateIso = paidDate,
                        onLinesChange = { draftLines = it },
                        onChangeGivenCentsChange = { draftChangeBsCents = it },
                        onChangeGivenUsdCentsChange = { draftChangeUsdCents = it },
                    ) { _, _, _, _ -> }
                    Spacer(Modifier.height(8.dp))
                    Text(
                        "Cada línea define su método y monto (Bs o $). Los totales se muestran en ambas monedas; no se convierte $ a Bs con la tasa para marcar pendiente.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                    // Resumen detallado antes de confirmar: métodos, vuelto, pendientes y total.
                    val montoPlanCents = parseBulkAmountToCents(amountInput)
                    val pagadoBsCents = draftLines
                        .filterNot { it.method.usesUsdAmount() }
                        .sumOf { parseBulkAmountToCents(it.amountInput) }
                    val desgloseCobro = buildBulkPaymentBreakdown(
                        lines = draftLines,
                        walletApplyCents = 0L,
                        discountBsCents = 0L,
                        discountUsdCents = 0L,
                        changeBsCents = draftChangeBsCents,
                        changeUsdCents = draftChangeUsdCents,
                        pendingBsCents = (montoPlanCents - pagadoBsCents).coerceAtLeast(0L),
                        pendingUsdCents = 0L,
                        dueBsCents = montoPlanCents,
                    )
                    if (desgloseCobro.isNotEmpty()) {
                        Spacer(Modifier.height(10.dp))
                        Text(
                            "Resumen antes de confirmar",
                            fontWeight = FontWeight.SemiBold,
                            color = AdminColors.Text,
                            fontSize = 13.sp,
                        )
                        Spacer(Modifier.height(6.dp))
                        BulkPaymentBreakdownList(desgloseCobro)
                    }
                }
                if (initial != null && twoCol) {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                        Column(Modifier.weight(1f)) {
                            PaymentFormLeftColumn(
                                method = method,
                                methodMenu = methodMenu,
                                onMethodMenu = { methodMenu = it },
                                onMethodSelect = { m ->
                                    method = m
                                    if (!m.requiresPayerPhone()) payerPhone = ""
                                },
                                amountInput = amountInput,
                                onAmountInput = { amountInput = it },
                                paidDate = paidDate,
                                onPaidDate = { paidDate = it },
                            )
                        }
                        Column(Modifier.weight(1f)) {
                            PaymentFormRightColumn(
                                method = method,
                                notes = notes,
                                onNotes = { notes = it },
                                duplicateRefAlert = mobileRefDuplicateAlert,
                                proofPayload = proofPayload,
                                proofName = proofName,
                                initial = initial,
                                saving = saving,
                                picking = picking,
                                onPickProof = {
                                    formError = null
                                    picking = true
                                    openProofFilePicker { picked ->
                                        if (picked == null) {
                                            proofHolder.clear()
                                            scope.launch {
                                                picking = false
                                                formError = "No se seleccionó ningún archivo."
                                            }
                                            return@openProofFilePicker
                                        }
                                        val payload = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
                                        proofHolder.set(payload, picked.fileName)
                                        scope.launch {
                                            picking = false
                                            proofPayload = payload
                                            proofName = picked.fileName
                                            formError = null
                                        }
                                    }
                                },
                                status = status,
                                statusMenu = statusMenu,
                                onStatusMenu = { statusMenu = it },
                                onStatusSelect = { status = it },
                                showStatus = true,
                            )
                        }
                    }
                } else if (initial != null) {
                    PaymentFormLeftColumn(
                        method = method,
                        methodMenu = methodMenu,
                        onMethodMenu = { methodMenu = it },
                        onMethodSelect = { m ->
                            method = m
                            if (!m.requiresPayerPhone()) payerPhone = ""
                        },
                        amountInput = amountInput,
                        onAmountInput = { amountInput = it },
                        paidDate = paidDate,
                        onPaidDate = { paidDate = it },
                    )
                    Spacer(Modifier.height(10.dp))
                    PaymentFormRightColumn(
                        method = method,
                        notes = notes,
                        onNotes = { notes = it },
                        duplicateRefAlert = mobileRefDuplicateAlert,
                        proofPayload = proofPayload,
                        proofName = proofName,
                        initial = initial,
                        saving = saving,
                        picking = picking,
                        onPickProof = {
                            formError = null
                            picking = true
                            openProofFilePicker { picked ->
                                if (picked == null) {
                                    proofHolder.clear()
                                    scope.launch {
                                        picking = false
                                        formError = "No se seleccionó ningún archivo."
                                    }
                                    return@openProofFilePicker
                                }
                                val payload = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
                                proofHolder.set(payload, picked.fileName)
                                scope.launch {
                                    picking = false
                                    proofPayload = payload
                                    proofName = picked.fileName
                                    formError = null
                                }
                            }
                        },
                        status = status,
                        statusMenu = statusMenu,
                        onStatusMenu = { statusMenu = it },
                        onStatusSelect = { status = it },
                        showStatus = true,
                    )
                }
                formError?.let {
                    Spacer(Modifier.height(8.dp))
                    Text(it, color = AdminColors.Error, fontSize = 12.sp)
                }
                Spacer(Modifier.height(18.dp))
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss, enabled = !saving) {
                        Text("Cancelar", color = AdminColors.TextMuted)
                    }
                    Spacer(Modifier.width(8.dp))
                    Button(
                        onClick = {
                            val cents = eurosToCents(amountInput)
                            val ref = notes.trim()
                            val paidIso = parseFlexibleDateToIso(paidDate)
                            when {
                                clientId.isBlank() -> formError = "Selecciona un cliente de la lista."
                                cents == null -> formError = "Monto inválido (usa formato 45,00)."
                                paidIso == null -> formError = "Fecha del pago inválida (DD/MM/AAAA o AAAA-MM-DD)."
                                initial == null -> {
                                    val lines = draftLines.ifEmpty {
                                        listOf(PaymentLineDraft(method = method, amountInput = amountInput))
                                    }
                                    for (line in lines) {
                                        if (line.amountCents(amountInput) <= 0) {
                                            formError = "Cada línea debe tener un monto válido."
                                            return@Button
                                        }
                                        paymentReferenceValidationError(line)?.let {
                                            formError = it
                                            return@Button
                                        }
                                    }
                                    val sum = lines.sumOf { it.amountCents(amountInput) }
                                    if (sum != cents) {
                                        formError = "La suma de líneas debe igualar el monto total."
                                        return@Button
                                    }
                                    formError = null
                                    onSave(
                                        AdminPaymentUpsert(
                                            clientId = clientId,
                                            method = lines.first().method,
                                            amountCents = cents,
                                            status = if (lines.any { it.method.requiresManualApproval() }) {
                                                AdminPaymentStatus.PENDING
                                            } else {
                                                AdminPaymentStatus.PAID
                                            },
                                            notes = ref.takeIf { it.isNotBlank() },
                                            paidAt = "${paidIso}T12:00:00Z",
                                        ),
                                        lines,
                                        null,
                                    )
                                }
                                method.requiresProofOrReference() && ref.isBlank() ->
                                    formError = if (method == AdminPaymentMethod.MOBILE_PAYMENT) {
                                        "Indica los últimos 5 dígitos de la referencia."
                                    } else {
                                        "La referencia es obligatoria para este método."
                                    }
                                method == AdminPaymentMethod.MOBILE_PAYMENT &&
                                    normalizePaymentReference(method, ref).length < 5 ->
                                    formError = "La referencia de pago móvil debe tener 5 dígitos (los últimos del comprobante)."
                                else -> {
                                    formError = null
                                    val normalizedRef = normalizePaymentReference(method, ref)
                                    onSave(
                                        AdminPaymentUpsert(
                                            clientId = clientId,
                                            method = method,
                                            amountCents = cents,
                                            status = status,
                                            notes = normalizedRef.takeIf { it.isNotBlank() },
                                            payerPhone = null,
                                            paidAt = "${paidIso}T12:00:00Z",
                                        ),
                                        null,
                                        proofHolder.payload ?: proofPayload,
                                    )
                                }
                            }
                        },
                        enabled = !saving,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text(if (saving) "…" else "Guardar")
                    }
                }
            }
        }
    }
}

@Composable
private fun PaymentFormLeftColumn(
    method: AdminPaymentMethod,
    methodMenu: Boolean,
    onMethodMenu: (Boolean) -> Unit,
    onMethodSelect: (AdminPaymentMethod) -> Unit,
    amountInput: String,
    onAmountInput: (String) -> Unit,
    paidDate: String,
    onPaidDate: (String) -> Unit,
) {
    FormLabel("Método")
    Box {
        OutlinedButton(onClick = { onMethodMenu(true) }, modifier = Modifier.fillMaxWidth()) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    adminPaymentMethodIcon(method),
                    contentDescription = null,
                    tint = AdminColors.Primary,
                    modifier = Modifier.size(18.dp),
                )
                Spacer(Modifier.width(8.dp))
                Text(method.labelEs(), color = AdminColors.Text)
            }
        }
        DropdownMenu(expanded = methodMenu, onDismissRequest = { onMethodMenu(false) }) {
            AdminPaymentMethod.adminChoices().forEach { m ->
                DropdownMenuItem(
                    text = { Text(m.labelEs()) },
                    onClick = {
                        onMethodSelect(m)
                        onMethodMenu(false)
                    },
                )
            }
        }
    }
    method.methodHint()?.let {
        Text(it, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 6.dp))
    }
    Spacer(Modifier.height(10.dp))
    FormLabel("${method.amountInputLabel()} *")
    FormField(amountInput, onAmountInput, "45,00")
    Spacer(Modifier.height(10.dp))
    FormLabel("Fecha del pago *")
    AdminDateField(
        value = paidDate,
        onValueChange = onPaidDate,
        placeholder = "AAAA-MM-DD o DD/MM/AAAA",
        outputFormat = AdminDateOutputFormat.ISO,
    )
    Text(
        "La referencia no puede repetirse el mismo día.",
        color = AdminColors.TextMuted,
        fontSize = 12.sp,
        modifier = Modifier.padding(top = 4.dp),
    )
}

@Composable
private fun PaymentFormRightColumn(
    method: AdminPaymentMethod,
    notes: String,
    onNotes: (String) -> Unit,
    duplicateRefAlert: String? = null,
    proofPayload: PickedImagePayload?,
    proofName: String?,
    initial: AdminPayment?,
    saving: Boolean,
    picking: Boolean,
    onPickProof: () -> Unit,
    status: AdminPaymentStatus,
    statusMenu: Boolean,
    onStatusMenu: (Boolean) -> Unit,
    onStatusSelect: (AdminPaymentStatus) -> Unit,
    showStatus: Boolean,
) {
    if (method.requiresProofOrReference()) {
        FormLabel(
            if (method == AdminPaymentMethod.MOBILE_PAYMENT) {
                "Referencia (últimos 5 dígitos) *"
            } else {
                "Referencia *"
            },
        )
        FormField(
            notes,
            { onNotes(if (method == AdminPaymentMethod.MOBILE_PAYMENT) paymentLineReferenceInput(method, it) else it) },
            if (method == AdminPaymentMethod.MOBILE_PAYMENT) "12345" else "Nº de referencia / operación",
        )
        if (!duplicateRefAlert.isNullOrBlank()) {
            Text(
                duplicateRefAlert,
                color = Color(0xFFE65100),
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        if (method == AdminPaymentMethod.MOBILE_PAYMENT) {
            Text(
                "Si varias personas pagan con el mismo pago móvil, usa la misma referencia y el mismo comprobante.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp, bottom = 8.dp),
            )
        } else {
            Spacer(Modifier.height(10.dp))
        }
        FormLabel("Comprobante (imagen o PDF)")
        Row(verticalAlignment = Alignment.CenterVertically) {
            FileUploadIconButton(
                onClick = onPickProof,
                tooltip = when {
                    proofName != null -> "Cambiar comprobante"
                    !initial?.proofUrl.isNullOrBlank() -> "Cambiar comprobante"
                    else -> "Subir comprobante (imagen o PDF)"
                },
                enabled = !saving && !picking,
                tint = AdminColors.Primary,
                icon = Icons.Outlined.Image,
            )
            if (proofName != null) {
                Spacer(Modifier.width(8.dp))
                Text(proofName, color = AdminColors.Text, fontSize = 12.sp)
            }
        }
        if (proofPayload == null && !initial?.proofUrl.isNullOrBlank()) {
            Spacer(Modifier.height(8.dp))
            ProofAttachmentView(
                proofUrl = initial?.proofUrl,
                proofContentType = initial?.proofContentType,
                label = "Comprobante actual",
                accent = AdminColors.Primary,
                muted = AdminColors.TextMuted,
                surface = AdminColors.Surface,
                text = AdminColors.Text,
                outline = AdminColors.Outline,
                compact = true,
            )
        }
    } else {
        FormLabel("Referencia (opcional)")
        FormField(notes, onNotes, "Nº de referencia / operación")
    }
    if (showStatus) {
        Spacer(Modifier.height(10.dp))
        FormLabel("Estado")
        Box {
            OutlinedButton(onClick = { onStatusMenu(true) }, modifier = Modifier.fillMaxWidth()) {
                Text(status.labelEs(), color = AdminColors.Text)
            }
            DropdownMenu(expanded = statusMenu, onDismissRequest = { onStatusMenu(false) }) {
                AdminPaymentStatus.adminChoices().forEach { s ->
                    DropdownMenuItem(
                        text = { Text(s.labelEs()) },
                        onClick = {
                            onStatusSelect(s)
                            onStatusMenu(false)
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun FormLabel(text: String) {
    Text(text, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(bottom = 4.dp))
}

private fun todayIsoLocalDate(): String = todayIsoDate()

@Composable
private fun FormField(value: String, onChange: (String) -> Unit, placeholder: String) {
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
